import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ADSETS_TAB_INITIAL_STATE, CUSTOM_PARAMS_TEMPLATE_ARRAY} from './adsetsTab.consts';
import {AdGroup} from 'common/src/modules/externalAds/externalAds.types';
import {IntegratedAccountOption} from './adsetsTab.types';
import {ImportCustomParamsJobResult} from 'common/src/modules/jobs/jobs.types';
import {SocialCampaignIntegration, SocialCampaignStatuses} from '../../campaigns.types';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {
  SocialAdsetsCustomParam,
  SocialAdsetsPageModalAction,
} from 'common/src/modules/social/social.types';

const adsetsTabSlice = createSlice({
  name: BreadcrumbName.CampaignAdSets,
  initialState: ADSETS_TAB_INITIAL_STATE,
  reducers: {
    resetAdsetsTab: () => ADSETS_TAB_INITIAL_STATE,
    updateAdGroups: (state, action: PayloadAction<AdGroup[]>) => {
      state.adGroups = action.payload;
    },
    _setLoading: (state, action: PayloadAction<boolean>) => {
      state._loading = action.payload;
    },
    setModalAction: (state, action: PayloadAction<SocialAdsetsPageModalAction | null>) => {
      state.modalAction = action.payload;
    },
    setIsActionsMenuOpen: (state, action?: PayloadAction<boolean>) => {
      state.isActionsMenuOpen = action?.payload || !state.isActionsMenuOpen;
    },
    setSelectedAdGroupIds: (state, action: PayloadAction<number[]>) => {
      state.selectedAdGroupIds = action.payload;
    },
    setActiveAdGroup: (state, action: PayloadAction<AdGroup | null>) => {
      state.activeAdGroup = action.payload;
    },
    setAddingIntegration: (state, action: PayloadAction<boolean>) => {
      state.addingIntegration = action.payload;
    },
    setImportCustomParamsResult: (
      state,
      action: PayloadAction<ImportCustomParamsJobResult | null>,
    ) => {
      state.importCustomParamsResult = action.payload;
    },
    setIntegratedAccountOptions: (state, action: PayloadAction<IntegratedAccountOption[]>) => {
      state.integratedAccountOptions = action.payload;
    },
    setIntegratedAccountOptionsLoading: (state, action: PayloadAction<boolean>) => {
      state.integratedAccountOptionsLoading = action.payload;
    },
    setAccountCampaigns: (state, action: PayloadAction<SocialCampaignIntegration[]>) => {
      state.accountCampaigns = action.payload;
    },
    setAccountCampaignsLoading: (state, action: PayloadAction<boolean>) => {
      state.accountCampaignsLoading = action.payload;
    },
    setConnectedAccountId: (state, action: PayloadAction<number | null>) => {
      state.connectedAccountId = action.payload;
    },
    setConnectedCampaignIds: (state, action: PayloadAction<string[]>) => {
      state.connectedCampaignIds = action.payload;
    },
    resetIntegrationModalState: (state) => {
      state.integratedAccountOptions = [];
      state.accountCampaigns = [];
      state.connectedCampaignIds = [];
      state.connectedAccountId = null;
    },
    setRemovingIntegration: (state, action: PayloadAction<boolean>) => {
      state.removingIntegration = action.payload;
    },
    setCustomParams: (
      state,
      action: PayloadAction<
        | SocialAdsetsCustomParam[]
        | ((customParams: SocialAdsetsCustomParam[]) => SocialAdsetsCustomParam[])
      >,
    ) => {
      if (typeof action.payload === 'function') {
        state.customParams = action.payload(state.customParams);
      } else {
        state.customParams = action.payload;
      }
    },
    resetCustomParamsModalState: (state) => {
      state.customParams = CUSTOM_PARAMS_TEMPLATE_ARRAY;
      state.modalAction = null;
    },

    updateCampaignStatuses: (state, action: PayloadAction<SocialCampaignStatuses>) => {
      state.campaignStatuses = action.payload;
    },
  },
});

export const {
  resetAdsetsTab,
  updateAdGroups,
  _setLoading,
  setModalAction,
  setIsActionsMenuOpen,
  setSelectedAdGroupIds,
  setActiveAdGroup,
  setAddingIntegration,
  setImportCustomParamsResult,
  setIntegratedAccountOptions,
  setIntegratedAccountOptionsLoading,
  setAccountCampaigns,
  setAccountCampaignsLoading,
  setConnectedAccountId,
  setConnectedCampaignIds,
  resetIntegrationModalState,
  setRemovingIntegration,
  setCustomParams,
  resetCustomParamsModalState,
  updateCampaignStatuses,
} = adsetsTabSlice.actions;

export default adsetsTabSlice.reducer;
